import React from 'react'
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  LayoutDashboard,
  ClipboardList,
  FileText,
  Calendar,
  Users,
  Settings,
  LogOut,
  Menu,
  X,
  Wrench,
  Building2,
  ChevronDown,
  Check,
  ArrowRightLeft,
  Megaphone,
  Briefcase,
  Star,
} from 'lucide-react'
import { useAuthStore } from '../store/authStore'
import { Button, garageApi, carCareApi, useLocalStorage } from '@besiktning/ui'
import type { Booking, InspectionRequest } from '@besiktning/ui'
import { Logo } from './Logo'
import NotificationBell from './NotificationBell'
import { t } from '../i18n'

// Hook to determine role based on current membership
function useCurrentMembershipRole(currentMembership: { type: 'garage' | 'car_care'; id: number } | null, memberships: Array<{ id: number; type: 'garage' | 'car_care'; role: string; business: { id: number; name: string; type: string; kommun?: string } }>) {
  const currentMembershipData = React.useMemo(() => {
    if (!currentMembership) return null
    return memberships.find((m: typeof memberships[0]) => m.id === currentMembership.id && m.type === currentMembership.type)
  }, [currentMembership, memberships])

  const role = currentMembershipData?.role || null
  const type = currentMembership?.type || null
  
  return {
    role,
    type,
    isGarage: type === 'garage',
    isCarCare: type === 'car_care',
    isOwner: role === 'owner',
    isManager: role === 'manager',
    isStaff: role === 'staff' || role === 'mechanic',
  }
}

// Sidebar counts. Same query keys as the bell and the list screens, so no extra
// requests. Requests: unquoted open ones that came in since the Requests page
// or the bell was last opened. Jobs: cancellation requests awaiting approval,
// plus live jobs booked since the Jobs page was last opened.
// ponytail: requests count caps at the API's first page (20).
function useNavBadges(membership: { type: 'garage' | 'car_care'; id: number } | null) {
  const { pathname } = useLocation()
  const isGarage = membership?.type === 'garage'
  const [jobsSeenAt, setJobsSeenAt] = useLocalStorage<number>(
    `${membership?.type}:${membership?.id}:jobsSeenAt`,
    Date.now(),
  )
  const onJobs = pathname.startsWith('/jobs')
  React.useEffect(() => {
    if (onJobs) setJobsSeenAt(Date.now())
    // setJobsSeenAt is a new function every render (useLocalStorage); listing it
    // here would re-run this effect forever and freeze rendering on /jobs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onJobs])

  // Same key as the bell, so opening either one clears the other.
  const [requestsSeenAt, setRequestsSeenAt] = useLocalStorage<number>(
    `${membership?.type}:${membership?.id}:requestsSeenAt`,
    Date.now(),
  )
  const onRequests = pathname.startsWith('/requests')
  React.useEffect(() => {
    if (onRequests) setRequestsSeenAt(Date.now())
    // Same reason as above: the setter changes identity every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onRequests])

  const { data: requests = [] } = useQuery({
    queryKey: ['requests', 'open'],
    queryFn: () => garageApi.getRequests({ status: 'open' }),
    refetchInterval: 60_000,
    enabled: isGarage,
  })
  const { data: bookings = [] } = useQuery({
    queryKey: ['jobs', isGarage ? 'garage' : 'car_care'],
    queryFn: () => (isGarage ? garageApi.getBookings() : carCareApi.getBookings()),
    refetchInterval: 60_000,
    enabled: !!membership,
  })

  const unquoted = (requests as InspectionRequest[]).filter(
    (r) =>
      !r.cancelled &&
      !onRequests &&
      Date.parse(r.created_at) > requestsSeenAt &&
      !(r.quotes ?? []).some((q) => String(q.garage_id) === String(membership?.id)),
  ).length
  const jobs = (bookings as Booking[]).filter(
    (b) =>
      (b.status as string) === 'pending_cancellation' ||
      (!onJobs && !['completed', 'cancelled'].includes(b.status) && Date.parse(b.created_at) > jobsSeenAt),
  ).length

  return { '/requests': isGarage ? unquoted : 0, '/jobs': jobs } as Record<string, number>
}

export default function Layout() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false)
  const [showAccountSwitcher, setShowAccountSwitcher] = React.useState(false)
  const { user, logout, memberships, currentMembership, selectMembership, requiresMembershipSelection } = useAuthStore()
  const navigate = useNavigate()
  const { isGarage, isOwner, isManager, role } = useCurrentMembershipRole(currentMembership, memberships)
  const badges = useNavBadges(currentMembership)

  // Auto-open account switcher if membership selection is required
  React.useEffect(() => {
    if (requiresMembershipSelection && memberships.length > 0) {
      setShowAccountSwitcher(true)
    }
  }, [requiresMembershipSelection, memberships])

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  const handleSelectMembership = async (membership: typeof memberships[0]) => {
    if (currentMembership?.type === membership.type && currentMembership?.id === membership.id) {
      setShowAccountSwitcher(false)
      return
    }
    
    try {
      await selectMembership(membership.type, membership.id)
      setShowAccountSwitcher(false)
      // Refresh the page to reload data for the new garage
      window.location.reload()
    } catch (error) {
      console.error('Failed to switch account:', error)
    }
  }

  const getCurrentBusinessName = () => {
    if (!currentMembership) return t('layout.selectCompany')
    const membership = memberships.find(m => m.id === currentMembership.id && m.type === currentMembership.type)
    return membership?.business.name || t('layout.selectCompany')
  }

  const getCurrentBusinessType = () => {
    if (!currentMembership) return t('layout.garage')
    const membership = memberships.find(m => m.id === currentMembership.id && m.type === currentMembership.type)
    return membership?.business.type || t('layout.garage')
  }

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'owner': return t('layout.roleOwner')
      case 'manager': return t('layout.roleManager')
      case 'mechanic': return t('layout.roleMechanic')
      case 'staff': return t('layout.roleStaff')
      default: return role
    }
  }

  // Build nav items based on membership type
  const navItems = React.useMemo(() => {
    const items = [
      { path: '/', icon: LayoutDashboard, label: 'Dashboard' },
    ]

    // Only show Requests and Quotes for garage (not car care)
    if (isGarage) {
      items.push(
        { path: '/requests', icon: ClipboardList, label: t('layout.navRequests') },
        { path: '/quotes', icon: FileText, label: t('layout.navQuotes') }
      )
    }

    // Show Calendar, Jobs, Services, and Reviews for both
    items.push(
      { path: '/calendar', icon: Calendar, label: t('layout.navCalendar') },
      { path: '/jobs', icon: Briefcase, label: t('layout.navJobs') },
      { path: '/services', icon: Wrench, label: t('layout.navServices') },
      { path: '/reviews', icon: Star, label: t('layout.navReviews') }
    )

    // Only show Staff and Ads for owners and managers
    if (isOwner || isManager) {
      items.push(
        { path: '/staff', icon: Users, label: t('layout.navStaff') },
        { path: '/ads', icon: Megaphone, label: t('layout.navAds') },
      )
    }

    items.push({ path: '/profile', icon: Settings, label: t('layout.navSettings') })

    return items
  }, [isGarage, isOwner, isManager])

  const hasMultipleMemberships = memberships.length > 1

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Mobile menu button */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
        <Logo className="h-9" />
        <div className="flex items-center gap-1">
          {isGarage && currentMembership && (
            <NotificationBell garageId={currentMembership.id} />
          )}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="p-2 rounded-lg hover:bg-gray-100"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Sidebar — pinned to the viewport on desktop so logout stays reachable
          however long the page is; the nav scrolls inside it if it overflows. */}
      <aside
        className={`
          fixed inset-y-0 left-0 z-40 w-64 bg-white border-r border-gray-200 transform transition-transform duration-200 ease-in-out flex-shrink-0
          ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
          lg:translate-x-0 lg:sticky lg:top-0 lg:bottom-auto lg:h-screen
          pt-16 lg:pt-0
        `}
      >
        <div className="h-full flex flex-col overflow-hidden">
          {/* Logo */}
          <div className="hidden lg:flex h-16 items-center px-6 border-b border-gray-200">
            <Logo className="h-10 sm:h-12" />
          </div>

          {/* Account Switcher - Always show current account info */}
          <div className="px-4 py-3 border-b border-gray-200">
            <button
              onClick={() => {
                setShowAccountSwitcher(!showAccountSwitcher)
              }}
              className={`w-full flex items-center justify-between p-3 rounded-lg transition-colors ${
                memberships.length > 0 
                  ? 'bg-blue-50 hover:bg-blue-100 cursor-pointer' 
                  : 'bg-gray-50 cursor-default'
              }`}
            >
              <div className="flex items-center min-w-0">
                <div className="h-10 w-10 rounded-lg bg-blue-600 flex items-center justify-center flex-shrink-0">
                  <Building2 className="h-5 w-5 text-white" />
                </div>
                <div className="ml-3 text-left min-w-0">
                  <p className="text-sm font-semibold text-gray-900 truncate">{getCurrentBusinessName()}</p>
                  <p className="text-xs text-gray-500">
                    {getCurrentBusinessType()} • {getRoleLabel(role || 'staff')}
                  </p>
                </div>
              </div>
              {memberships.length > 0 && (
                <ChevronDown className={`h-4 w-4 text-gray-400 flex-shrink-0 transition-transform ${showAccountSwitcher ? 'rotate-180' : ''}`} />
              )}
            </button>

            {/* Account List Dropdown */}
            {memberships.length > 0 && showAccountSwitcher && (
              <div className="mt-2 space-y-1 max-h-64 overflow-y-auto">
                <p className="text-xs font-medium text-gray-400 uppercase tracking-wider px-3 py-2">
                  {t('layout.selectOtherCompany')}
                </p>
                {memberships.map((membership) => {
                  const isSelected = currentMembership?.type === membership.type && currentMembership?.id === membership.id
                  return (
                    <button
                      key={`${membership.type}-${membership.id}`}
                      onClick={() => handleSelectMembership(membership)}
                      className={`w-full flex items-center justify-between p-3 rounded-lg text-left transition-colors ${
                        isSelected 
                          ? 'bg-blue-100 border border-blue-300' 
                          : 'hover:bg-gray-100 border border-transparent'
                      }`}
                    >
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{membership.business.name}</p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${
                            membership.type === 'garage' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'
                          }`}>
                            {membership.type === 'garage' ? t('layout.garage') : t('layout.carCare')}
                          </span>
                          <span className="text-xs text-gray-500">{getRoleLabel(membership.role)}</span>
                        </div>
                      </div>
                      {isSelected && (
                        <div className="h-6 w-6 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0 ml-2">
                          <Check className="h-4 w-4 text-white" />
                        </div>
                      )}
                    </button>
                  )
                })}
              </div>
            )}
          </div>

          {/* Navigation */}
          {/* min-h-0 lets this shrink below its content height, so a long nav
              scrolls here instead of pushing the logout footer off-screen. */}
          <nav className="flex-1 min-h-0 px-4 py-4 space-y-1 overflow-y-auto">
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.path === '/'}
                onClick={() => setIsMobileMenuOpen(false)}
                className={({ isActive }) =>
                  `flex items-center px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-blue-50 text-blue-700'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`
                }
              >
                <item.icon className="mr-3 h-5 w-5" />
                <span className="flex-1">{item.label}</span>
                {badges[item.path] > 0 && (
                  <span className="min-w-[20px] h-5 px-1.5 rounded-full bg-red-600 text-white text-xs font-semibold flex items-center justify-center">
                    {badges[item.path] > 9 ? '9+' : badges[item.path]}
                  </span>
                )}
              </NavLink>
            ))}

            {/* Switch Account Menu Item - Alternative way to access */}
            {memberships.length > 1 && (
              <>
                <div className="my-2 border-t border-gray-200" />
                <button
                  onClick={() => setShowAccountSwitcher(!showAccountSwitcher)}
                  className={`w-full flex items-center px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                    showAccountSwitcher
                      ? 'bg-blue-50 text-blue-700'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <ArrowRightLeft className="mr-3 h-5 w-5" />
                  <span className="flex-1 text-left">{t('layout.switchCompany')}</span>
                  <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
                    {memberships.length}
                  </span>
                </button>
              </>
            )}
          </nav>

          {/* User info & logout */}
          <div className="p-4 border-t border-gray-200">
            <div className="flex items-center mb-3">
              <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 font-medium">
                {user?.name?.charAt(0) || user?.email?.charAt(0) || 'U'}
              </div>
              <div className="ml-3 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{user?.name || user?.email}</p>
                {!hasMultipleMemberships && (
                  <p className="text-xs text-gray-500">
                    {user?.garage_memberships?.[0]?.garage?.name || t('layout.garage')}
                  </p>
                )}
              </div>
            </div>
            <Button
              variant="ghost"
              className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50"
              onClick={handleLogout}
            >
              <LogOut className="mr-2 h-4 w-4" />
              {t('layout.logout')}
            </Button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 pt-16 lg:pt-0 min-h-screen w-full overflow-y-auto">
        {/* Desktop header. The sidebar is only 256px wide and clips its
            overflow, so the bell lives here where its panel has room. */}
        {isGarage && currentMembership && (
          <div className="hidden lg:flex sticky top-0 z-30 h-16 items-center justify-end px-8 bg-white border-b border-gray-200">
            <NotificationBell garageId={currentMembership.id} />
          </div>
        )}
        <div className="p-4 lg:p-8 max-w-7xl mx-auto">
          <Outlet />
        </div>
      </main>

      {/* Mobile menu overlay */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-30 lg:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}
    </div>
  )
}
